import {
  add,
  endOfDay,
  isBefore,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";
import { RMFlightWrapper, TimeSelect } from "../../types";

export function generateDateRange(
  effectiveFrom: number,
  effectiveTo: number,
  dateMin: Date,
  dateMax: Date,
  frequency: string
) {
  const start = startOfDay(effectiveFrom);
  const end = endOfDay(effectiveTo);
  const newDates = [];
  for (let i = 0; i < 7; i++) {
    const date = add(dateMin, { days: i });
    const isInInterval = isWithinInterval(date, { start, end });
    const day = date.getDay() !== 0 ? date.getDay().toString() : "7";
    newDates.push({
      date: date,
      scheduled: isInInterval && frequency.indexOf(day) !== -1,
    });
  }
  return newDates;
}

export function convertTimeSelect(select: TimeSelect): string {
  if (select === TimeSelect.Morning) {
    return "00:01";
  } else if (select === TimeSelect.Afternoon) {
    return "11:00";
  } else if (select === TimeSelect.Evening) {
    return "17:00";
  } else {
    return "00:01";
  }
}

export function generateFlightKey(flight: RMFlightWrapper) {
  if (flight.connxLeg3OperatedBy !== null) {
    return (
      flight.operatedBy +
      flight.flightNo +
      "#" +
      flight.connxLeg2OperatedBy +
      flight.connxLeg2FlightNo +
      "#" +
      flight.connxLeg3OperatedBy +
      flight.connxLeg3FlightNo +
      flight.departureMinuteOfDay
    );
  } else if (flight.connxLeg2OperatedBy !== null) {
    return (
      flight.operatedBy +
      flight.flightNo +
      "#" +
      flight.connxLeg2OperatedBy +
      flight.connxLeg2FlightNo +
      flight.departureMinuteOfDay
    );
  } else {
    return (
      flight.operatedBy + flight.flightNo + "#" + flight.departureMinuteOfDay
    );
  }
}

/**
 * Parses a pipe-delimited seat class override string from customer config.
 * e.g. "CLASS_F|Premium|CLASS_E|Economy" → { CLASS_F: "Premium", CLASS_E: "Economy" }
 */
export function parseSeatClassOverrides(
  translateSeatClass?: string
): Record<string, string> {
  if (!translateSeatClass) return {};
  const parts = translateSeatClass.split("|");
  const overrides: Record<string, string> = {};
  for (let i = 0; i + 1 < parts.length; i += 2) {
    overrides[parts[i]] = parts[i + 1];
  }
  return overrides;
}

/**
 * Returns the cabin class label for a given i18n key.
 * - If departure is strictly before the transition date and a pre-transition override exists → pre-transition label
 * - If departure is on/after the transition date and a post-transition override exists → post-transition label
 * - Otherwise falls back to t(key).
 */
export function getClassLabel(
  key: string,
  departureDate: number | Date,
  transitionDateStr: string | undefined,
  preOverrides: Record<string, string>,
  postOverrides: Record<string, string>,
  t: (key: string) => string
): string {
  if (transitionDateStr) {
    const transitionDateOnly = startOfDay(parseISO(transitionDateStr));
    if (isBefore(startOfDay(departureDate), transitionDateOnly)) {
      if (preOverrides[key]) return preOverrides[key];
    } else {
      if (postOverrides[key]) return postOverrides[key];
    }
  }
  return t(key);
}
